import { Grid, IconButton, Typography, Theme, Box, Dialog, Skeleton, DialogContent, DialogActions, Button } from '@mui/material';
import { Form, Formik, FormikProps } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { ReportsRightLayout } from '../../InvestorReport/index';
import { MFSubmitButton } from '../../../lib/formik';
import { useEffect, useRef, useState } from 'react';
import dayjs from "dayjs";
import { ReportsListObj, ReportsListRes } from '../../../redux-store/types/api-types';
import MFSelectField from '../../../lib/formik/SelectField';
import * as Yup from 'yup';
import { DayJsDatePicker } from '../../onboarding/newDatePicker';
import { RootStateType } from '../../../redux-store/reducers';
import { getCGStatementReport, getReportsInvestorList } from '../../../redux-store/actions/reports';
import { DialogCloseIcon, YellowWarning, SuccessIcon, FailureIcon } from '../../../assets/SvgIcons';
import { reportDownload } from '../../Reports/report-utils';
import { maskInfo } from '../../../utils/utilityFunctions';
import FormikSearchDropdown from '../../../lib/formik/FormikSearchDropdown';
import { CONTENT_TYPE } from '../../../utils/constant';

interface Values {
    selectedInvestor: ReportsListObj | null;
    startDate: string;
    endDate: string;
    downloadOption?: string
    shareVia?: string;
}

export function CapitalGainStatement({
    handleSideSectionDisabled
}: {
    handleSideSectionDisabled: (value: boolean) => void
}): JSX.Element {
    const formikRef = useRef<FormikProps<Values>>(null);

    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const currentDate = dayjs();
    const [loading, setLoading] = useState<boolean>(false);
    const { iwappRmId } = useSelector((state: RootStateType) => state.id);
    const [investorListData, setInvestorListData] = useState<ReportsListRes>({} as ReportsListRes);
    const [isNoInvestorDialogVisible, setNoInvestorDialogVisible] = useState<boolean>(false)
    const [isDownloading, setIsDownloading] = useState<boolean>(false);
    const { isNavbarInfoLoading, businessDate } = useSelector((state: RootStateType) => state.navbarInfo);
    const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState<boolean>(false)
    const [isErrorDialogOpen, setIsErrorDialogOpen] = useState<boolean>(false)
    const [errorMessage, setErrorMessage] = useState<string>("")

    const fetchInvestorList = async () => {
        try {
            setLoading(true);
            const payload = {
                rmId: `${iwappRmId}`
            }
            const response = await (dispatch(getReportsInvestorList(payload)) as unknown as Promise<ReportsListRes>);
            setInvestorListData(response);
            if (!response?.isSuccess) {
                setNoInvestorDialogVisible(true)
            }
        } catch (e) {
            console.log((e as Error).message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchInvestorList();
    }, [])

    const todayDate = new Date();
    const formattedBusinessDate = businessDate?.length ? dayjs(businessDate, "DD/MM/YYYY").format("YYYY-MM-DD") : dayjs(todayDate).format("YYYY-MM-DD");

    const downloadOptions = [
        { key: 'PDF', value: 'pdf' },
        { key: 'Excel', value: 'excel' },
    ]

    const shareViaOptions = [
        { key: 'Email', value: 'email' },
        { key: 'Download', value: 'download' },
    ]
    const initialValues: Values = {
        selectedInvestor: null,
        downloadOption: '',
        startDate: formattedBusinessDate,
        endDate: formattedBusinessDate,
        shareVia: '',
    };

    const cgValidationSchema = Yup.object().shape({
        selectedInvestor: Yup
            .object()
            .nullable()
            .test(
                "investor-required",
                "Please select an investor",
                function (value) { return !!value }
            ),
        downloadOption: Yup.string().required('Please select download option'),
        shareVia: Yup.string().required('Please select share or download'),
        startDate: Yup
            .date()
            .required('From Date is required')
            .typeError('From Date is required')
            .min(new Date("1950-01-01"), "From Date should not be less than 01/01/1950")
            .max(formattedBusinessDate, "From Date should not be greater than system's date")
            .test('fromDateTest', 'From Date cannot be greater than To Date', function (value) {
                const { endDate } = this.parent;
                return !endDate || !value || value <= endDate;
            }),
        endDate: Yup
            .date()
            .required('To Date is required')
            .typeError('To Date is required')
            .min(new Date("1950-01-01"), "To Date should not be less than 01/01/1950")
            .max(formattedBusinessDate, "To Date should not be greater than system's date")
            .test('toDateTest', 'To Date cannot be less than From Date', function (value) {
                const { startDate } = this.parent;
                return !startDate || !value || value >= startDate;
            }),
    })


    const handleSubmit = async ({
        selectedInvestor,
        startDate,
        endDate,
        downloadOption,
        shareVia
    }: Values) => {
        try {
            setIsDownloading(true)
            handleSideSectionDisabled(true)
            const payload = {
                investorName: selectedInvestor?.customerName ?? '',
                start: dayjs(startDate, "YYYY-MM-DD").format("DD/MM/YYYY"),
                end: dayjs(endDate, "YYYY-MM-DD").format("DD/MM/YYYY"),
                isPDF: (downloadOption ?? "").toLowerCase() === 'pdf' ? true : false,
                shareVia: (shareVia ?? "").toLowerCase() === 'email' ? 'email' : 'download',
                panNo: selectedInvestor?.panNo ?? '',
                //adding clientCode
                clientCode: selectedInvestor?.clientCode ?? '',
            }

            const response = await (dispatch(getCGStatementReport(payload)) as unknown as Promise<any>)
            if (shareVia?.toLowerCase() === 'download') {
                if (response.type) {
                    setIsSuccessDialogOpen(true)
                    reportDownload(response as unknown as Blob, `CGStatementReport.${response.type === CONTENT_TYPE.PDF ? 'pdf' : 'xlsx'}`);
                }
                else {
                    setIsErrorDialogOpen(true)
                    setErrorMessage('File download failed. Please try again.')
                }
            }
            else if (shareVia?.toLowerCase() === 'email') {
                setIsSuccessDialogOpen(true)
            }
        } catch (error) {
            console.log("Error: ", error)
        } finally {
            setIsDownloading(false)
            handleSideSectionDisabled(false)
        }
    }

    const handleNoInvestorDialogClose = () => {
        setNoInvestorDialogVisible(false)
    }

    const handleInvestorChange = (selectedObj: Record<string, any>) => {
        if (!formikRef.current) return

        const { values, setValues } = formikRef.current ?? {}

        if (!selectedObj) {
            setValues({
                ...values,
                selectedInvestor: null,
            })

            return
        }

        setValues({
            ...values,
            selectedInvestor: selectedObj as ReportsListObj,
        })
    }

    return (
        <>
            <ReportsRightLayout>
                <Typography
                    variant='h2'
                    sx={{
                        color: 'text.primary',
                        lineHeight: '25px',
                        letterSpacing: '0.01px',
                        mb: { xs: '20px', sm: '40px' },
                    }}>
                    Capital Gain Statement
                </Typography>
                <Formik
                    innerRef={formikRef}
                    validationSchema={cgValidationSchema}
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    enableReinitialize={true}
                >
                    {({ values, setFieldValue }) => {
                        return (
                            <Form>
                                <Grid container spacing={3} >
                                    {/* <Grid item container columnSpacing={3} justifyContent={'flex-start'}> */}
                                    <Grid item xs={12} lg={6}>
                                        {(loading || isNavbarInfoLoading) && <Skeleton
                                            animation={'wave'}
                                            height={90}

                                        />}
                                        {(!loading && !isNavbarInfoLoading) && (
                                            <FormikSearchDropdown
                                                name='selectedInvestor'
                                                label='Select Investor *'
                                                options={(investorListData?.result ?? [])?.map((obj: ReportsListObj) => ({
                                                    ...obj,
                                                    label: `${maskInfo(obj.customerName)} (${obj?.clientCode ?? ""}) ${obj?.accountType ? " - " + obj.accountType : ""}`,
                                                    value: obj.clientCode
                                                }))}
                                                onChange={handleInvestorChange}
                                                disabled={loading || isDownloading || isNavbarInfoLoading}
                                            />
                                        )}
                                    </Grid>
                                    <Grid item xs={12} lg={6}>
                                        {(loading || isNavbarInfoLoading) && <Skeleton
                                            animation={'wave'}
                                            height={90}

                                        />}
                                        {(!loading && !isNavbarInfoLoading) && (
                                            <DayJsDatePicker
                                                id=""
                                                name='startDate'
                                                label="From Date *"
                                                maxDate={new Date(formattedBusinessDate)}
                                                disabled={loading || isDownloading || isNavbarInfoLoading}
                                                currentDate={formattedBusinessDate}
                                            />
                                        )}
                                    </Grid>
                                    <Grid item xs={12} lg={6}>
                                        {(loading || isNavbarInfoLoading) && <Skeleton
                                            animation={'wave'}
                                            height={90}

                                        />}
                                        {(!loading && !isNavbarInfoLoading) && (
                                            <DayJsDatePicker
                                                id=""
                                                name='endDate'
                                                label="To Date *"
                                                minDate={new Date(values.startDate)}
                                                maxDate={new Date(formattedBusinessDate)}
                                                disabled={loading || isDownloading || isNavbarInfoLoading}
                                                currentDate={formattedBusinessDate}
                                            />
                                        )}
                                    </Grid>
                                    <Grid item xs={12} lg={6}>
                                        {(loading || isNavbarInfoLoading) && <Skeleton
                                            animation={'wave'}
                                            height={90}

                                        />}
                                        {(!loading && !isNavbarInfoLoading) && <MFSelectField
                                            name={'downloadOption'}
                                            label="Select Download Option"
                                            items={downloadOptions}
                                            value={values.downloadOption}
                                            onChange={(e) => {
                                                setFieldValue('downloadOption', e.target.value);
                                            }}
                                            disabled={isDownloading}
                                        />}
                                    </Grid>
                                    <Grid item xs={12} lg={6}>
                                        {(isNavbarInfoLoading) && <Skeleton
                                            animation={'wave'}
                                            height={90}

                                        />}
                                        {(!isNavbarInfoLoading) && <MFSelectField
                                            name={'shareVia'}
                                            label="Select Share or Download"
                                            items={shareViaOptions}
                                            value={values.shareVia}
                                            onChange={(e) => {
                                                setFieldValue('shareVia', e.target.value);
                                            }}
                                            disabled={isDownloading}
                                        />}
                                    </Grid>
                                    <Grid item container columnSpacing={3} justifyContent={"flex-end"} sx={{ mt: 2 }}>
                                        <Grid item xs={12} lg={6}>
                                            {(loading || isNavbarInfoLoading) && <Skeleton
                                                animation={'wave'}
                                                height={90}

                                            />}
                                            {(!loading && !isNavbarInfoLoading) && <MFSubmitButton
                                                label="Proceed"
                                                loadingEnable={isDownloading}
                                            />}
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Form>
                        )
                    }}
                </Formik>
                <Dialog
                    aria-labelledby="success-dialog"
                    open={isSuccessDialogOpen}
                    PaperProps={{
                        sx: (theme: Theme) => ({
                            maxWidth: '550px',
                            width: "100%",
                            borderRadius: "12px",
                            padding: "24px",
                            backgroundColor: theme.palette.common.white
                        }),
                    }}
                >
                    <DialogContent
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            rowGap: "12px",
                            padding: "15px",
                            overflow: "hidden"
                        }}
                    >
                        <SuccessIcon />
                        <Typography
                            variant="h2"
                            sx={(theme: Theme) => ({
                                lineHeight: "25px",
                                letterSpacing: "0.01px",
                                color: theme.palette.text.primary
                            })}
                        >
                            Success!
                        </Typography>
                        <Typography
                            variant="subtitle_400"
                            sx={(theme: Theme) => ({
                                lineHeight: "22px",
                                letterSpacing: "0.02px",
                                color: theme.palette.text.secondary,
                                textAlign: "center",
                                paddingLeft: "10px",
                                paddingRight: "10px"
                            })}
                        >
                            {formikRef.current?.values.shareVia === 'email' ? 'The statement has been  shared successfully.' : 'The statement has been downloaded successfully.'}
                        </Typography>
                    </DialogContent>
                    <DialogActions
                        sx={{
                            padding: "0px",
                            marginTop: "40px",
                        }}
                    >
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={() => setIsSuccessDialogOpen(false)}
                            sx={(theme: Theme) => ({
                                ...theme.typography.body_500,
                                fontWeight: 600,
                                lineHeight: "20px",
                                color: theme.palette.common.white,
                                borderRadius: "4px",
                                padding: "13px 20px",
                                backgroundColor: theme.palette.primary[500]
                            })}
                        >
                            Okay
                        </Button>
                    </DialogActions>
                </Dialog>
                <Dialog
                    aria-labelledby="error-dialog"
                    open={isErrorDialogOpen}
                    PaperProps={{
                        sx: (theme: Theme) => ({
                            maxWidth: '550px',
                            width: "100%",
                            borderRadius: "12px",
                            padding: "24px",
                            backgroundColor: theme.palette.common.white
                        }),
                    }}
                >
                    <DialogContent
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            rowGap: "12px",
                            padding: "15px",
                            overflow: "hidden"
                        }}
                    >
                        <FailureIcon width="35" height="35" />
                        <Typography
                            variant="h2"
                            sx={(theme: Theme) => ({
                                lineHeight: "25px",
                                letterSpacing: "0.01px",
                                color: theme.palette.text.primary
                            })}
                        >
                            Error!
                        </Typography>
                        <Typography
                            variant="subtitle_400"
                            sx={(theme: Theme) => ({
                                lineHeight: "22px",
                                letterSpacing: "0.02px",
                                color: theme.palette.text.secondary,
                                textAlign: "center",
                                paddingLeft: "10px",
                                paddingRight: "10px"
                            })}
                        >
                            {errorMessage}
                        </Typography>
                    </DialogContent>
                    <DialogActions
                        sx={{
                            padding: "0px",
                            marginTop: "40px",
                        }}
                    >
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={() => setIsErrorDialogOpen(false)}
                            sx={(theme: Theme) => ({
                                ...theme.typography.body_500,
                                fontWeight: 600,
                                lineHeight: "20px",
                                color: theme.palette.common.white,
                                borderRadius: "4px",
                                padding: "13px 20px",
                                backgroundColor: theme.palette.primary[500]
                            })}
                        >
                            Okay
                        </Button>
                    </DialogActions>
                </Dialog>
            </ReportsRightLayout>
            <Dialog
                onClose={handleNoInvestorDialogClose}
                open={isNoInvestorDialogVisible}
                maxWidth='sm'
                sx={(theme: Theme) => ({
                    '& .MuiPaper-root': {
                        borderRadius: '12px',
                        backgroundColor: theme.palette.common.white,
                        padding: "22px 24px",
                        width: "533px",
                    },
                })}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        rowGap: "8px",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center"
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                columnGap: "12px",
                                alignItems: "center"
                            }}
                        >
                            <YellowWarning />
                            <Typography
                                variant="h2"
                                sx={(theme: Theme) => ({ color: theme.palette.text.primary })}
                            >
                                No Investor Found!!
                            </Typography>
                        </Box>
                        <IconButton
                            aria-label="close"
                            onClick={handleNoInvestorDialogClose}
                            sx={{
                                padding: "5px"
                            }}
                        >
                            <DialogCloseIcon />
                        </IconButton>
                    </Box>
                </Box>
            </Dialog>
        </>
    );
};


